import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Link, useParams } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import moment from 'moment';

// COMPONENTS
import PageName from '../../components/PageName';
import Button from '../../components/Button';
import CircleImage from '../../components/CircleImage';
import DisplayDateTime from '../../components/DisplayDateTime';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import LoadingSpinner from '../../components/LoadingSpinner';
import GlobalModal from '../../components/GlobalModal';
import AcceptModal from './AcceptModal';
import AcknowledgeModal from './AcknowledgeModal';
import DisplayDate from '../../components/DisplayDate';

// ICONS
import { ReactComponent as DownloadIcon } from '../../assets/svg/download.svg';
import { ReactComponent as TickIcon } from '../../assets/svg/tick.svg';
import { ReactComponent as TickSuccessIcon } from '../../assets/svg/tick-success.svg';
import { ReactComponent as EnvelopeIcon } from '../../assets/svg/envelope-icon.svg';

// TYPES
import { IncidentEditCommand, DomainIncidentType } from '../../api/models';
import { RouteParams } from '../../Interfaces';

const IncidentReportsDetail: React.FC = () => {
	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Get all incident Types from Redux store
	const incidentTypes = useAppSelector((state) => state.lists.incidentTypes);
	const incidentSpecifications = useAppSelector((state) => state.lists.incidentSpecifications) || [];

	// get ID from params
	const params = useParams<RouteParams>();
	const incidentId = params.id;

	// State to control modals
	const [showAcceptModal, setShowAcceptModal] = React.useState<boolean>(false);
	const [showAcknowledgeModal, setShowAcknowledgeModal] = React.useState<boolean>(false);
	const [modalId, setModalId] = React.useState<string>('');

	const openModal = (id: string) => {
		setModalId(id);

		setShowAcceptModal(true);
	};

	// Set up data state and initial data
	const initialIncidentReportsDetailData: IncidentEditCommand = {
		id: '',
		summaryUrl: '',
		creator: '',
		// creatorRole: '',
		// createdDateTime: '',
		identifier: undefined,
		canEdit: false,
		canAcknowledge: false,
		isAcknowledged: false,
		allAcknowledged: false,
		contacts: [],
		contactNotifiedDate: '',
		// leadersNotified: [],

		// STEP ONE
		name: '',
		age: '',
		title: '',
		occurrence: '',
		type: undefined,
		specification: undefined,
		location: '',
		description: '',
		witness: false,
		witnesses: [],

		// STEP TWO
		firstAidAdministered: false,
		firstAid: '',
		medicationAdministered: false,
		medication: [],
		medicalPersonnelContacted: false,
		medicalPersonnel: [],
		emergencyServiceContacted: false,
		emergencyServices: [],
		takenToHospital: false,
		hospital: '',
		dateAndTimeTakenToHospital: '',
		personTakenToHospital: '',
		relationshipToChild: '',
		otherActions: false,
		otherActionsTaken: '',

		// STEP THREE
		individualsNotified: true,
		individuals: [],
		authorityNotified: true,
		authorities: [],
		notes: '',
		createAttachments: [],
		attachments: [],
		deleteAttachmentIds: [],

		leaderRecipientIds: [],
		otherRecipientIds: [],

		otherRecipientEmails: [],

		otherRecipients: [],
		leaders: [],

		// STEP FOUR:
		reviewer: '',
		reviewerRole: ''
	};

	const [incidentReportsDetailData, setIncidentReportsDetailData] = React.useState<IncidentEditCommand>(
		initialIncidentReportsDetailData
	);

	const getIncidentReportsDetailsData = async () => {
		setIsLoading(true);

		const response = await apibridge.getIncidentReportsDetailsData(incidentId);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = initialIncidentReportsDetailData } = response;

			setIsLoading(false);

			setIncidentReportsDetailData(result);
		}
	};

	React.useEffect(() => {
		getIncidentReportsDetailsData();
	}, []);

	const {
		// DETAILS FIELDS
		id,
		summaryUrl,
		contacts,
		creator,
		creatorRole,
		// createdDateTime,
		identifier,
		contactNotifiedDate,

		canEdit,
		canAcknowledge,
		isAcknowledged,
		allAcknowledged,
		// leadersNotified,

		// FORM FIELDS
		name,
		age,
		dateOfBirth,
		title,
		occurrence,
		type,
		specification,
		specificationName,
		location,
		description,
		witness,
		witnesses,
		firstAidAdministered,
		firstAid,
		medicationAdministered,
		medication,
		medicalPersonnelContacted,
		medicalPersonnel,
		emergencyServiceContacted,
		emergencyServices,
		takenToHospital,
		hospital,
		dateAndTimeTakenToHospital,
		personTakenToHospital,
		relationshipToChild,
		otherActions,
		otherActionsTaken,
		individualsNotified,
		individuals,
		authorityNotified,
		authorities,
		notes,
		attachments,
		reviewer,
		reviewerRole,
		created
	} = incidentReportsDetailData;

	const displayIncidentType = (type?: DomainIncidentType) => {
		if (type) {
			const currentType = incidentTypes?.find((item) => item.value === type);
			return currentType?.label;
		}
	};

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<PageName title={title} showBack={true} />

								<div className="complaints-detail-general-info">
									<div className="general-info-left">
										<div className="info-col">
											<div className="info-title">Submitted by</div>

											<div className="info-detail">{creator}</div>
										</div>

										<div className="info-col">
											<div className="info-title">Incident ID</div>

											<div className="info-detail">{identifier}</div>
										</div>

										<div className="info-col">
											<div className="info-title">Category</div>

											<div className="info-detail">
												{displayIncidentType(type)} - {specificationName}
											</div>
										</div>
									</div>

									<div className="general-info-right">
										{isOrganisationActive && canEdit && (
											<Link className="btn-primary resolve-btn" to={`/incident-submit/${id}`}>
												Edit
											</Link>
										)}

										{isOrganisationActive && canAcknowledge && (
											<Button
												className="btn-primary resolve-btn mleft-10"
												onClick={() => setShowAcknowledgeModal(true)}
												disabled={isAcknowledged}
											>
												Acknowledge
											</Button>
										)}
									</div>
								</div>

								<div className="two-col-layout">
									<div className="complaints-detail w-50-layout">
										<h3>Incident details</h3>

										<div className="general-divider"></div>

										<div className="detail-block row">
											<div className="col-5">
												<h4 className="detail-heading">Child's name</h4>

												<p className="detail-info">{name}</p>
											</div>

											<div className="col-6">
												<h4 className="detail-heading">Child's age</h4>

												<p className="detail-info">{age}</p>
											</div>
										</div>

										{dateOfBirth ? (
											<div className="detail-block">
												<h4 className="detail-heading">Child's date of birth</h4>

												<time className="detail-info">
													<DisplayDate>{dateOfBirth}</DisplayDate>
												</time>
											</div>
										) : null}

										<div className="detail-block">
											<h2 className="detail-heading">Incident title</h2>

											<p className="detail-info">{title}</p>
										</div>

										{identifier ? (
											<div className="detail-block">
												<h2 className="detail-heading">Incident ID</h2>

												<p className="detail-info">{identifier}</p>
											</div>
										) : null}

										<div className="detail-block">
											<h4 className="detail-heading">Date and time of incident</h4>

											<time className="detail-info">
												<DisplayDateTime>{occurrence}</DisplayDateTime>
											</time>
										</div>

										<div className="detail-block">
											<h4 className="detail-heading">Incident type</h4>

											<p className="detail-info">
												{displayIncidentType(type)} - {specificationName}
											</p>
										</div>

										<div className="detail-block">
											<h4 className="detail-heading">Location of the incident</h4>

											<p className="detail-info">{location}</p>
										</div>

										<div className="detail-block">
											<h4 className="detail-heading">Description of the incident</h4>

											<p className="detail-info">{description}</p>
										</div>

										<div className="detail-block">
											<h4 className="detail-heading">Incident witnesses</h4>

											{witness ? (
												<>
													{witnesses &&
														witnesses.length > 0 &&
														witnesses.map((item, index) => {
															return (
																<div key={index} className="row incident-review-detail-row">
																	<div className="col-5">
																		<p className="name">{item.name}</p>

																		<p>{item.role}</p>
																	</div>

																	<div className="col-6">
																		<p>{item.email}</p>

																		<p>{item.phone}</p>
																	</div>
																</div>
															);
														})}
												</>
											) : (
												<p className="detail-info">None</p>
											)}
										</div>

										{/* ACTIONS TAKEN */}
										<h3>Actions taken</h3>

										<div className="general-divider"></div>

										<div className="detail-block">
											<h4 className="detail-heading">First aid administered</h4>

											<p className="detail-info">{firstAidAdministered ? <>{firstAid}</> : 'None'}</p>
										</div>

										<div className="detail-block">
											<h4 className="detail-heading">Medication administered</h4>

											{medicationAdministered ? (
												<>
													{medication &&
														medication.length > 0 &&
														medication.map((item, index) => {
															return (
																<div key={index} className="row incident-review-detail-row">
																	<div className="col-12">
																		<p className="name">{item.name}</p>
																	</div>

																	<div className="col-5">
																		<p>Dosage of {item.dosage}</p>

																		<p>
																			<DisplayDateTime>{item.dateAndTime}</DisplayDateTime>
																		</p>
																	</div>

																	<div className="col-6">
																		<p>by {item.personAdministered}</p>

																		<p>{item.role}</p>
																	</div>
																</div>
															);
														})}
												</>
											) : (
												<p className="detail-info">None</p>
											)}
										</div>

										<div className="detail-block">
											<h4 className="detail-heading">Medical personnel contacted</h4>

											{medicalPersonnelContacted ? (
												<>
													{medicalPersonnel &&
														medicalPersonnel.length > 0 &&
														medicalPersonnel.map((item, index) => {
															return (
																<div key={index} className="row incident-review-detail-row">
																	<div className="col-5">
																		<p className="name">{item.name}</p>

																		<p>{item.role}</p>
																	</div>

																	<div className="col-6">
																		<p>
																			Contacted <DisplayDateTime>{item.dateAndTime}</DisplayDateTime>
																		</p>

																		<p>{item.phoneOrEmail}</p>
																	</div>
																</div>
															);
														})}
												</>
											) : (
												<p className="detail-info">None</p>
											)}
										</div>

										<div className="detail-block">
											<h4 className="detail-heading">Emergency services contacted</h4>

											{emergencyServiceContacted ? (
												<>
													{emergencyServices &&
														emergencyServices.length > 0 &&
														emergencyServices.map((item, index) => {
															return (
																<div key={index} className="row incident-review-detail-row">
																	<div className="col-5">
																		<p className="name">{item.name}</p>
																	</div>

																	<div className="col-6">
																		<p>
																			Contacted <DisplayDateTime>{item.dateAndTime}</DisplayDateTime>
																		</p>

																		{item.attend ? (
																			<p>
																				Arrived <DisplayDateTime>{item.dateAndTime}</DisplayDateTime>
																			</p>
																		) : (
																			<p>Did not attend</p>
																		)}
																	</div>
																</div>
															);
														})}
												</>
											) : (
												<p className="detail-info">None</p>
											)}
										</div>

										<div className="detail-block">
											<h4 className="detail-heading">Transported to hospital</h4>

											{takenToHospital ? (
												<>
													<p className="detail-info">{hospital}</p>

													<p className="detail-info">
														<DisplayDateTime>{dateAndTimeTakenToHospital}</DisplayDateTime>
													</p>

													<p className="detail-info">
														by {personTakenToHospital}, {relationshipToChild}
													</p>
												</>
											) : (
												<p className="detail-info">None</p>
											)}
										</div>

										<div className="detail-block">
											<h4 className="detail-heading">Other actions taken</h4>

											<p className="detail-info">{otherActions ? <>{otherActionsTaken}</> : 'None'}</p>
										</div>

										{/* NOTIFICATION */}

										<h3>Notification</h3>

										<div className="general-divider"></div>

										<div className="detail-block">
											<h4 className="detail-heading">Individuals notified</h4>

											{individualsNotified ? (
												<>
													{individuals &&
														individuals.length > 0 &&
														individuals.map((item, index) => {
															return (
																<div key={index} className="row incident-review-detail-row dark-text">
																	<div className="col-12">
																		<p className="name">{item.name}</p>
																	</div>

																	<div className="col-5">
																		<p>{item.role}</p>

																		<p>
																			<DisplayDateTime>{item.dateAndTime}</DisplayDateTime>
																		</p>
																	</div>

																	<div className="col-6">
																		<p>{item.contactMethod}</p>

																		<p>Notification {item.isNotifiedSuccess ? 'successful' : 'unsuccessful'}</p>
																	</div>
																</div>
															);
														})}
												</>
											) : (
												<p className="detail-info">None</p>
											)}
										</div>

										<div className="detail-block">
											<h4 className="detail-heading">Authorities notified</h4>

											{authorityNotified ? (
												<>
													{authorities &&
														authorities.length > 0 &&
														authorities.map((item, index) => {
															return (
																<div key={index} className="row incident-review-detail-row dark-text">
																	<div className="col-5">
																		<p className="name">{item.authorityName}</p>

																		<p>{item.notifiedName}</p>

																		<p>
																			<DisplayDateTime>{item.dateAndTime}</DisplayDateTime>
																		</p>
																	</div>

																	<div className="col-6">
																		<p>{item.reason}</p>

																		<p>{item.contactMethod}</p>

																		<p>Notification {item.isNotifiedSuccess ? 'successful' : 'unsuccessful'}</p>
																	</div>
																</div>
															);
														})}
												</>
											) : (
												<p className="detail-info">None</p>
											)}
										</div>

										{/* ADDITIONAL INFORMATION */}
										{notes || (attachments && attachments.length > 0) ? (
											<>
												<h3>Additional information</h3>
												<div className="general-divider"></div>
											</>
										) : null}

										{notes ? (
											<div className="detail-block">
												<h4 className="detail-heading">Notes</h4>

												<p className="detail-info">{notes}</p>
											</div>
										) : null}

										{attachments && attachments.length > 0 && (
											<div className="detail-block">
												<h4 className="detail-heading">Attachments</h4>

												<ul>
													{attachments &&
														attachments.length > 0 &&
														attachments.map((item, index) => {
															return (
																<li key={item.id || index}>
																	<a className="btn btn-secondary mtop-40 download-btn" href={item.link || ''} download>
																		<DownloadIcon className="link-icon" />
																		{item.displayFileName}
																	</a>
																</li>
															);
														})}
												</ul>
											</div>
										)}

										{/* PERSON WHO REVIEWED THIS REPORT */}
										<h3 className="mtop-30">Person who reviewed this report</h3>

										<div className="general-divider"></div>

										<div className="detail-block row">
											<div className="col-5">
												<h4 className="detail-heading">Reviewed by</h4>

												<p className="detail-info">{reviewer}</p>
											</div>

											<div className="col-6">
												<h4 className="detail-heading">Role</h4>

												<p className="detail-info">{reviewerRole}</p>
											</div>
										</div>

										{/* PERSON WHO CREATED THIS REPORT */}
										<h3>Person who created this report</h3>

										<div className="general-divider"></div>

										<div className="detail-block row">
											<div className="col-5">
												<h4 className="detail-heading">Reported by</h4>

												<p className="detail-info">{creator}</p>
											</div>

											<div className="col-6">
												<h4 className="detail-heading">Role</h4>

												<p className="detail-info">{creatorRole}</p>
											</div>
										</div>

										<div className="incident-electronic-acknowledgement">
											<p className="sub-grey-text">Electronic Signature Acknowledgement</p>

											<p className="signature">{creator}</p>
										</div>

										<div className="detail-block">
											<h4 className="detail-heading">Report signed and created on</h4>

											<p className="detail-info">
												<DisplayDateTime>{created}</DisplayDateTime>
											</p>
										</div>
									</div>

									<div className="notified-contacts-container">
										<div className="notified-contacts-box">
											<div className="notified-contact-header">
												<div className="row">
													<div className="col-6">
														<h2>Notified contacts</h2>
													</div>

													<div className="col-3">
														<div className="d-flex flex-column align-items-center">
															<h2>Notified</h2>

															{contactNotifiedDate ? (
																<div className="d-flex flex-column align-items-center">
																	<p className="sub-grey-text body-tiny text-center">
																		<DisplayDateTime format="DD/MM/YYYY hh:mm a">{contactNotifiedDate}</DisplayDateTime>
																	</p>
																</div>
															) : null}
														</div>
													</div>

													<div className="col-3">
														<div className="d-flex justify-content-center">
															<p className={`${allAcknowledged ? 'purple-text' : null} acknowledged-text`}>
																Acknowledged
															</p>
														</div>
													</div>
												</div>
											</div>

											<ul>
												{contacts &&
													contacts.length > 0 &&
													contacts.map((item, index) => {
														return (
															<li key={index} className="row notified-contact-row">
																{item.isExternal ? (
																	<>
																		<div className="col-2 contact-profile">
																			<EnvelopeIcon />
																		</div>

																		<div className="col-8 contact-info">
																			<p className="email">{item.email}</p>
																			{isOrganisationActive && item.isAcknowledged && item.canAcknowledge && (
																				<div className="button-group">
																					{item.acknowledgmentDocument ? (
																						<>
																							<a
																								href={item.acknowledgmentDocument}
																								className="btn-text pdl-0"
																								download="Acknowledge Document"
																							>
																								View
																							</a>
																							/
																						</>
																					) : null}

																					<Button
																						className={`btn-text ${item.acknowledgmentDocument ? '' : 'pdl-0'}`}
																						onClick={() => openModal(item.recipientId || '')}
																					>
																						Update Document
																					</Button>
																				</div>
																			)}
																		</div>

																		<div className="col-2 contact-action">
																			{item.isAcknowledged ? (
																				<TickSuccessIcon className="tick-icon" />
																			) : isOrganisationActive && item.canAcknowledge ? (
																				<Button className="btn-text" onClick={() => openModal(item.recipientId || '')}>
																					Accept
																				</Button>
																			) : null}
																		</div>
																	</>
																) : (
																	<>
																		<div className="col-2 col-md-1 contact-profile pl-0">
																			<CircleImage
																				size={40}
																				firstName={item.firstName}
																				lastName={item.lastName}
																				src={item.profileImage}
																			/>
																		</div>

																		<div className="col-4 col-md-5 contact-info">
																			<p className="name">
																				{item.firstName} {item.lastName} {item.isYou ? <strong>(You)</strong> : null}
																			</p>

																			<p className="role">{item.role}</p>
																		</div>

																		<div className="col-3 contact-action">
																			{item.hasNotified ? (
																				<TickSuccessIcon className="tick-icon" />
																			) : (
																				<TickIcon className="tick-icon" />
																			)}
																		</div>

																		<div className="col-3 contact-action">
																			{item.isAcknowledged ? (
																				<TickSuccessIcon className="tick-icon" />
																			) : (
																				<TickIcon className="tick-icon" />
																			)}
																		</div>
																	</>
																)}
															</li>
														);
													})}
											</ul>

											<div className="notified-contact-guide">
												<strong>Please note: </strong>
												Manually accepting a report on behalf of a recipient requires the uploading of a signed
												document.
											</div>
										</div>

										<a className="btn btn-secondary mtop-40 download-btn w-100" href={`${summaryUrl}`} download>
											<DownloadIcon className="link-icon" />
											Download Incident Summary
										</a>
									</div>
								</div>

								<GlobalModal isOpen={showAcceptModal} setIsOpen={setShowAcceptModal} titleText="Accept on behalf of...">
									<AcceptModal recipientId={modalId} getIncidentReportsDetailsData={getIncidentReportsDetailsData} />
								</GlobalModal>

								<GlobalModal
									isOpen={showAcknowledgeModal}
									setIsOpen={setShowAcknowledgeModal}
									titleText="Acknowledge incident report"
								>
									<AcknowledgeModal
										getIncidentReportsDetailsData={getIncidentReportsDetailsData}
										incidentId={incidentId}
										title={title}
									/>
								</GlobalModal>

								<ScrollToTopButton offsetValue={100} />
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default IncidentReportsDetail;
